import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl, getCsrfToken } from '../api';
import './EmployeeRoster.css';

const EMPTY_FORM = {
    full_name: '',
    job_title: '',
    supervisor_status: 'not_set',
    hire_date: '',
    supervisor_since: '',
    work_location: '',
    preferred_language: ''
};

const getEmployeeId = (employee) => employee.id ?? employee.employee_id;

const formatDate = (value) => {
    if (!value) return null;

    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }).format(date);
};

const isSupervisor = (status) => status === 'supervisor' || status === true || status === 1;

const getTrainingRoleLabel = (status) => {
    if (isSupervisor(status)) return 'Supervisor';
    if (status === 'non_supervisor') return 'Non-supervisor';
    return 'Training role not set';
};

const readApiResponse = async (response, fallbackMessage) => {
    let data;

    try {
        data = await response.json();
    } catch (error) {
        throw new Error(fallbackMessage);
    }

    if (response.ok === false || data.success !== true) {
        throw new Error(data.error || data.message || fallbackMessage);
    }

    return data;
};

const EmployeeRoster = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [actionError, setActionError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [archiveCandidate, setArchiveCandidate] = useState(null);
    const [archivingId, setArchivingId] = useState(null);

    const loadEmployees = useCallback(async ({ showLoading = true } = {}) => {
        if (showLoading) setInitialLoading(true);
        setLoadError('');

        try {
            const response = await fetch(apiUrl('/api/employees'), {
                credentials: 'include'
            });
            const data = await readApiResponse(response, 'We could not load the employee roster.');

            if (!Array.isArray(data.employees)) {
                throw new Error('The employee roster response was not in the expected format.');
            }

            setEmployees(data.employees);
            return true;
        } catch (error) {
            setLoadError(error.message || 'We could not load the employee roster.');
            return false;
        } finally {
            if (showLoading) setInitialLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    const activeEmployees = useMemo(
        () => employees.filter((employee) => (employee.status || 'active') === 'active'),
        [employees]
    );

    const handleChange = (event) => {
        const { name, value } = event.target;

        setActionError('');
        setFormData((current) => ({
            ...current,
            [name]: value,
            ...(name === 'supervisor_status' && value !== 'supervisor'
                ? { supervisor_since: '' }
                : {})
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setActionError('');
        setSuccessMessage('');

        const payload = {
            full_name: formData.full_name.trim(),
            job_title: formData.job_title.trim(),
            supervisor_status: formData.supervisor_status,
            hire_date: formData.hire_date || null,
            supervisor_since: formData.supervisor_since || null,
            work_location: formData.work_location.trim() || null,
            preferred_language: formData.preferred_language.trim() || null
        };

        try {
            const csrfToken = await getCsrfToken();
            const response = await fetch(apiUrl('/api/employees'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            });
            const data = await readApiResponse(response, 'We could not add this employee.');
            const employeeName = data.employee?.full_name || payload.full_name;

            setFormData(EMPTY_FORM);
            setSuccessMessage(`${employeeName} was added to the roster.`);
            await loadEmployees({ showLoading: false });
        } catch (error) {
            setActionError(error.message || 'We could not add this employee.');
        } finally {
            setSaving(false);
        }
    };

    const requestArchive = (employee) => {
        setActionError('');
        setSuccessMessage('');
        setArchiveCandidate(employee);
    };

    const cancelArchive = () => {
        setArchiveCandidate(null);
    };

    const confirmArchive = async () => {
        if (!archiveCandidate) return;

        const employeeId = getEmployeeId(archiveCandidate);
        const employeeName = archiveCandidate.full_name;
        setArchivingId(employeeId);
        setActionError('');
        setSuccessMessage('');

        try {
            const csrfToken = await getCsrfToken();
            const response = await fetch(apiUrl(`/api/employees/${employeeId}`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include',
                body: JSON.stringify({ status: 'archived' })
            });
            await readApiResponse(response, 'We could not archive this employee.');

            setArchiveCandidate(null);
            setSuccessMessage(`${employeeName} was archived and removed from the active roster.`);
            await loadEmployees({ showLoading: false });
        } catch (error) {
            setActionError(error.message || 'We could not archive this employee.');
        } finally {
            setArchivingId(null);
        }
    };

    return (
        <main className="employee-roster">
            <div className="employee-roster__shell">
                <header className="employee-roster__header">
                    <p className="employee-roster__eyebrow">People and records</p>
                    <h1>Employee roster</h1>
                    <p id="employee-roster-purpose" className="employee-roster__intro">
                        This shared roster keeps employer-entered employee information in one place. SB 553 and
                        harassment-training requirements remain separate. CompCleared does not determine which laws
                        apply, what training is required, or whether your organization has met any legal obligation.
                        It does not assign training, calculate legal deadlines, or prove that training was completed.
                    </p>
                </header>

                <section className="employee-roster__panel" aria-labelledby="add-employee-heading">
                    <div className="employee-roster__section-heading">
                        <div>
                            <h2 id="add-employee-heading">Add an employee</h2>
                            <p>Fields marked with an asterisk are required.</p>
                        </div>
                    </div>

                    {actionError && <div className="employee-roster__alert employee-roster__alert--error" role="alert">{actionError}</div>}
                    {successMessage && <div className="employee-roster__alert employee-roster__alert--success" role="status">{successMessage}</div>}

                    <form className="employee-roster__form" onSubmit={handleSubmit} aria-describedby="employee-roster-purpose">
                        <div className="employee-roster__field">
                            <label htmlFor="employee-full-name">Full name <span aria-hidden="true">*</span></label>
                            <input
                                id="employee-full-name"
                                name="full_name"
                                type="text"
                                autoComplete="name"
                                value={formData.full_name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="employee-roster__field">
                            <label htmlFor="employee-job-title">Job title <span aria-hidden="true">*</span></label>
                            <input
                                id="employee-job-title"
                                name="job_title"
                                type="text"
                                autoComplete="organization-title"
                                value={formData.job_title}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="employee-roster__field">
                            <label htmlFor="employee-supervisor-status">Training role (employer-entered)</label>
                            <select
                                id="employee-supervisor-status"
                                name="supervisor_status"
                                value={formData.supervisor_status}
                                onChange={handleChange}
                                aria-describedby="employee-supervisor-status-help"
                            >
                                <option value="not_set">Not set</option>
                                <option value="supervisor">Supervisor</option>
                                <option value="non_supervisor">Non-supervisor</option>
                            </select>
                            <span id="employee-supervisor-status-help" className="employee-roster__help">
                                Choose from employer records. CompCleared does not determine supervisory status.
                            </span>
                        </div>

                        <div className="employee-roster__field">
                            <label htmlFor="employee-hire-date">Hire date <span className="employee-roster__optional">Optional</span></label>
                            <input
                                id="employee-hire-date"
                                name="hire_date"
                                type="date"
                                value={formData.hire_date}
                                onChange={handleChange}
                            />
                        </div>

                        {formData.supervisor_status === 'supervisor' && (
                            <div className="employee-roster__field">
                                <label htmlFor="employee-supervisor-since">Supervisor since <span className="employee-roster__optional">Optional</span></label>
                                <input
                                    id="employee-supervisor-since"
                                    name="supervisor_since"
                                    type="date"
                                    value={formData.supervisor_since}
                                    onChange={handleChange}
                                />
                            </div>
                        )}

                        <div className="employee-roster__field">
                            <label htmlFor="employee-location">Worksite label <span className="employee-roster__optional">Optional</span></label>
                            <input
                                id="employee-location"
                                name="work_location"
                                type="text"
                                autoComplete="organization"
                                value={formData.work_location}
                                onChange={handleChange}
                                placeholder="e.g., Oakland office"
                                aria-describedby="employee-location-help"
                            />
                            <span id="employee-location-help" className="employee-roster__help">
                                Use a business worksite label; do not enter a home address.
                            </span>
                        </div>

                        <div className="employee-roster__field">
                            <label htmlFor="employee-language">Preferred training language <span className="employee-roster__optional">Optional</span></label>
                            <input
                                id="employee-language"
                                name="preferred_language"
                                type="text"
                                value={formData.preferred_language}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="employee-roster__form-actions">
                            <button className="employee-roster__button employee-roster__button--primary" type="submit" disabled={saving}>
                                {saving ? 'Adding employee…' : 'Add employee'}
                            </button>
                        </div>
                    </form>
                </section>

                <section className="employee-roster__panel" aria-labelledby="active-employees-heading">
                    <div className="employee-roster__section-heading employee-roster__section-heading--roster">
                        <div>
                            <h2 id="active-employees-heading">Active employees</h2>
                            <p>Archived employees are not shown in this active roster.</p>
                        </div>
                        {!initialLoading && !loadError && (
                            <span className="employee-roster__count" aria-label={`${activeEmployees.length} active employees`}>
                                {activeEmployees.length}
                            </span>
                        )}
                    </div>

                    {initialLoading && (
                        <div className="employee-roster__state" role="status">Loading employee roster…</div>
                    )}

                    {!initialLoading && loadError && (
                        <div className="employee-roster__state employee-roster__state--error" role="alert">
                            <p>{loadError}</p>
                            <button className="employee-roster__button employee-roster__button--secondary" type="button" onClick={() => loadEmployees()}>
                                Try again
                            </button>
                        </div>
                    )}

                    {!initialLoading && !loadError && activeEmployees.length === 0 && (
                        <div className="employee-roster__state">
                            <h3>No active employees yet</h3>
                            <p>Add an employee above to start organizing roster records.</p>
                        </div>
                    )}

                    {!initialLoading && !loadError && activeEmployees.length > 0 && (
                        <ul className="employee-roster__list" aria-label="Active employee roster">
                            {activeEmployees.map((employee) => {
                                const employeeId = getEmployeeId(employee);
                                const supervisor = isSupervisor(employee.supervisor_status);
                                const trainingRole = getTrainingRoleLabel(employee.supervisor_status);
                                const hireDate = formatDate(employee.hire_date);
                                const supervisorSince = formatDate(employee.supervisor_since);
                                const confirmationOpen = getEmployeeId(archiveCandidate || {}) === employeeId;

                                return (
                                    <li className="employee-roster__employee" key={employeeId}>
                                        <div className="employee-roster__employee-main">
                                            <div className="employee-roster__employee-heading">
                                                <div>
                                                    <h3>{employee.full_name}</h3>
                                                    <p>{employee.job_title}</p>
                                                </div>
                                                <span className={`employee-roster__badge ${supervisor ? 'employee-roster__badge--supervisor' : ''}`}>
                                                    {trainingRole}
                                                </span>
                                            </div>

                                            <dl className="employee-roster__details">
                                                {employee.work_location && (
                                                    <div>
                                                        <dt>Worksite</dt>
                                                        <dd>{employee.work_location}</dd>
                                                    </div>
                                                )}
                                                {employee.preferred_language && (
                                                    <div>
                                                        <dt>Preferred training language</dt>
                                                        <dd>{employee.preferred_language}</dd>
                                                    </div>
                                                )}
                                                {hireDate && (
                                                    <div>
                                                        <dt>Hire date</dt>
                                                        <dd>{hireDate}</dd>
                                                    </div>
                                                )}
                                                {supervisor && supervisorSince && (
                                                    <div>
                                                        <dt>Supervisor since</dt>
                                                        <dd>{supervisorSince}</dd>
                                                    </div>
                                                )}
                                            </dl>
                                        </div>

                                        {!confirmationOpen ? (
                                            <div className="employee-roster__employee-actions">
                                                <button
                                                    className="employee-roster__button employee-roster__button--secondary"
                                                    type="button"
                                                    onClick={() => navigate(`/harassment-training?employee_id=${employeeId}`)}
                                                >
                                                    Harassment records
                                                </button>
                                                <button
                                                    className="employee-roster__button employee-roster__button--archive"
                                                    type="button"
                                                    onClick={() => requestArchive(employee)}
                                                    aria-expanded="false"
                                                    aria-controls={`archive-confirmation-${employeeId}`}
                                                >
                                                    Archive
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                id={`archive-confirmation-${employeeId}`}
                                                className="employee-roster__archive-confirmation"
                                                role="group"
                                                aria-label={`Confirm archiving ${employee.full_name}`}
                                            >
                                                <p>Archive {employee.full_name}? They will be removed from the active roster.</p>
                                                <div className="employee-roster__archive-actions">
                                                    <button
                                                        className="employee-roster__button employee-roster__button--secondary"
                                                        type="button"
                                                        onClick={cancelArchive}
                                                        disabled={archivingId === employeeId}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="employee-roster__button employee-roster__button--danger"
                                                        type="button"
                                                        onClick={confirmArchive}
                                                        disabled={archivingId === employeeId}
                                                    >
                                                        {archivingId === employeeId ? 'Archiving…' : 'Yes, archive employee'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </div>
        </main>
    );
};

export default EmployeeRoster;
